import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eye, Users, ArrowLeft, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfileViews() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [viewers, setViewers] = useState([]);
  const [viewed, setViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('viewers');

  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const tabActive = isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white';
  const tabInactive = isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300';

  useEffect(() => {
    fetchProfileViews();
  }, []);

  const fetchProfileViews = async () => {
    try {
      const response = await axios.get(`${API}/profile/views`);
      setViewers(response.data.viewers || []);
      setViewed(response.data.viewed || []);
    } catch (error) {
      console.error('Error fetching profile views:', error);
      // Mock data for demo
      setViewers([]);
      setViewed([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const currentList = activeTab === 'viewers' ? viewers : viewed;

  return (
    <div className={`min-h-screen ${bg}`} data-testid="profile-views-page">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <Link to="/" className={`inline-flex items-center gap-2 ${textSecondary} hover:${textPrimary} mb-4`}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
          <h1 className={`text-3xl font-bold ${textPrimary}`}>Profile Views Analytics</h1>
          <p className={textSecondary}>See who viewed your profile and track your engagement.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('viewers')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'viewers' ? tabActive : tabInactive}`}
            data-testid="tab-viewers"
          >
            <Eye size={16} className="inline mr-2" />
            Who Viewed Me ({viewers.length})
          </button>
          <button
            onClick={() => setActiveTab('viewed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'viewed' ? tabActive : tabInactive}`}
            data-testid="tab-viewed"
          >
            <Users size={16} className="inline mr-2" />
            Profiles I Viewed ({viewed.length})
          </button>
        </div>

        {loading ? (
          <Card className={`p-8 text-center ${cardBg}`}>
            <p className={textSecondary}>Loading...</p>
          </Card>
        ) : currentList.length === 0 ? (
          <Card className={`p-8 text-center ${cardBg}`}>
            <Eye size={48} className={`mx-auto mb-4 ${textMuted}`} />
            <h3 className={`text-lg font-semibold mb-2 ${textPrimary}`}>
              {activeTab === 'viewers' ? 'No profile views yet' : 'You haven\'t viewed any profiles yet'}
            </h3>
            <p className={`mb-4 ${textSecondary}`}>
              {activeTab === 'viewers' 
                ? 'Complete your profile and stay active to attract more investors.' 
                : 'Browse the Discovery page to find potential investors.'}
            </p>
            <Link to={activeTab === 'viewers' ? '/profile-setup' : '/discovery'}>
              <Button className="btn-primary">
                {activeTab === 'viewers' ? 'Complete Profile' : 'Discover Investors'}
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {currentList.map((item, index) => (
              <Card key={index} className={`p-4 ${cardBg}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-600' : 'bg-slate-200'}`}>
                      <Users size={24} className={textMuted} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>{item.name}</h3>
                      <p className={`text-sm ${textMuted}`}>{item.role} - {item.location}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`flex items-center gap-1 text-xs ${textMuted}`}>
                      <Clock size={12} />
                      {formatDate(item.viewed_at)}
                    </div>
                    <Link to={`/profile/${item.user_id}`}>
                      <Button variant="outline" size="sm" className="mt-2">
                        View Profile
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
